import { useState, useEffect, createContext, useContext } from "react";
import { safeSet, safeGet, safeRemove } from "../utils/safeStorage";
import axios from "axios";
import { forgetListsOnPhone } from "../utils/offlineWrites";
import { getLanguage, t } from "@/i18n";
import {
  isSignedOutAnswer, keepsBodyPass, refreshBody, loadSignedInUser, meRetryDelay,
  forgetThisPhonesPush, thisPhonesPushSubscription,
} from "../utils/sessionRules";
import { isFramedPreview } from "../utils/phonePreview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Configure axios defaults
axios.defaults.timeout = 15000; // 15 second timeout

// Track if we're currently handling a 401
let isHandling401 = false;
let refreshPromise = null;

// Add retry interceptor for failed requests
axios.interceptors.response.use(
  response => response,
  async (error) => {
    const config = error.config;
    
    // Don't retry if no config or already retried twice
    if (!config || config._retryCount >= 2) {
      return Promise.reject(error);
    }
    
    // Don't retry auth endpoints or 401/403 errors
    if (config.url?.includes('/auth/') || error.response?.status === 401 || error.response?.status === 403) {
      return Promise.reject(error);
    }
    
    // Network errors or 5xx errors - retry with exponential backoff
    if (!error.response || (error.response.status >= 500 && error.response.status < 600)) {
      config._retryCount = (config._retryCount || 0) + 1;
      const delay = 1000 * config._retryCount; // 1s, 2s
      await new Promise(resolve => setTimeout(resolve, delay));
      return axios(config);
    }
    
    return Promise.reject(error);
  }
);


// ---- Session refresh plumbing (hybrid) ----
// Browsers/PWA: refresh token lives in an httpOnly cookie (xel_refresh) the
// JS can never read - withCredentials makes the browser send it to /api/auth.
// Native (Capacitor): cookie storage is unreliable in WKWebView, so the app
// sends X-Client: native and keeps the refresh token in localStorage instead.
const IS_NATIVE = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
// Cookies only matter off-native (browser/PWA, same-origin). Keeping
// withCredentials off for native avoids credentialed-CORS pitfalls entirely.
axios.defaults.withCredentials = !IS_NATIVE;
if (IS_NATIVE) axios.defaults.headers.common["X-Client"] = "native";

// Native only (V19.13, audit A3). On the web the pass is the cookie; a body
// pass stored here (Google and Facebook used to send one) was sent again
// after the cookie had moved on, and signed the person out.
const saveRefreshToken = (data) => {
  if (keepsBodyPass(IS_NATIVE) && data?.refresh_token) safeSet("refreshToken", data.refresh_token);
};
if (!IS_NATIVE) safeRemove("refreshToken");

// This phone's push subscription, registered again with the server. After a
// password change the server drops every phone's push; this one stays signed
// in, so it puts itself back (V19.13, audit B8). Best-effort.
const reRegisterThisPhonesPush = () => {
  const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
  thisPhonesPushSubscription(sw, safeGet("memberName"))
    .then((body) => (body ? axios.post(`${API}/push/subscribe`, body) : null))
    .catch(() => {});
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(safeGet("token"));
  const [loading, setLoading] = useState(true);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [showMemberSetup, setShowMemberSetup] = useState(false);
  const [memberName, setMemberName] = useState(safeGet("memberName") || "");
  const [memberPhone, setMemberPhone] = useState(safeGet("memberPhone") || "");
  const [memberBadge, setMemberBadge] = useState(safeGet("memberBadge") || "");
  // Dark mode is a single source of truth: this AuthContext state.
  // Initialization checks two localStorage keys for compatibility - the
  // canonical 'darkMode' key, plus a legacy 'theme' key that an older
  // version of SettingsPage wrote (which has since been removed). If the
  // legacy key is present we migrate it once, then drop it.
  const [darkMode, setDarkMode] = useState(() => {
    const canonical = safeGet("darkMode");
    if (canonical !== null) return canonical === "true";
    const legacy = safeGet("theme"); // 'dark' | 'light' | null
    if (legacy !== null) {
      const isDark = legacy === "dark";
      // Migrate to canonical key and drop the legacy one so nothing else
      // can read stale state and cause the previous two-system fight.
      safeSet("darkMode", isDark.toString());
      safeRemove("theme");
      return isDark;
    }
    return false;
  });
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [backendAwake, setBackendAwake] = useState(false);

  // Warm up backend on app load (handles Render cold starts)
  useEffect(() => {
    const warmUpBackend = async () => {
      try {
        await axios.get(`${API}/health`, { timeout: 10000 });
        setBackendAwake(true);
      } catch (e) {
        // Silent fail - just trying to wake up backend
        // Will retry on next API call
      }
    };
    warmUpBackend();
  }, []);

  // Add 401 interceptor for session expiry handling
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      response => response,
      async (error) => {
        // Handle 401 - try a silent refresh first, then fall back to the
        // session-expired modal. Admin and auth endpoints are exempt.
        const url = error.config?.url || '';
        const onAdminPage = window.location.pathname.startsWith('/admin');
        const exempt = onAdminPage || url.includes('/admin/') || url.includes('/auth/');
        if (error.response?.status === 401 && !exempt && !error.config?._retried && isFramedPreview()) {
          // The phone preview frame (V19.19) never renews the sign-in - the
          // page around it does. If that page has renewed it since this call
          // went out, try once more with the new pass; otherwise just fail
          // this call, with no "session expired" message.
          const stored = safeGet("token");
          const used = String(error.config?.headers?.Authorization || "").replace(/^Bearer\s+/i, "");
          if (stored && stored !== used) {
            const cfg = { ...error.config, _retried: true };
            cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${stored}` };
            return axios(cfg);
          }
          return Promise.reject(error);
        }
        if (error.response?.status === 401 && !exempt && !error.config?._retried) {
          try {
            if (!refreshPromise) {
              refreshPromise = axios.post(`${API}/auth/refresh`, refreshBody(IS_NATIVE, safeGet("refreshToken")))
                .finally(() => { setTimeout(() => { refreshPromise = null; }, 0); });
            }
            const res = await refreshPromise;
            const newToken = res.data.token;
            safeSet("token", newToken);
            saveRefreshToken(res.data);
            setToken(newToken);
            axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
            const cfg = { ...error.config, _retried: true };
            cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${newToken}` };
            return axios(cfg);
          } catch (refreshErr) {
            // Only a 401 from /auth/refresh ends the session (V19.13, audit
            // A4). No network, a 503 or the rate limit is not an answer: keep
            // the pass, and the next call tries again.
            if (isSignedOutAnswer(refreshErr)) {
              safeRemove("refreshToken");
              if (!isHandling401) {
                isHandling401 = true;
                setShowSessionExpired(true);
                setTimeout(() => { isHandling401 = false; }, 1000);
              }
            }
          }
        }
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  // Apply the `.dark` class to <html> based on state. We also write the
  // canonical localStorage key here (in addition to toggleDarkMode) so the
  // class and storage can never drift out of sync - and clear any legacy
  // 'theme' key that might be re-introduced by an old cached build.
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    safeSet("darkMode", darkMode.toString());
    if (safeGet("theme") !== null) {
      safeRemove("theme");
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode((prev) => !prev);
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // A signed-in account is never a first-time visitor: re-pin the intro-tour
  // flag whenever a session exists. iOS evicts localStorage after ~7 days of
  // Safari inactivity (and under low disk), which wiped both the token and
  // onboardingComplete together - so after logging back in, the app bounced a
  // veteran user into the first-time tour (v16.08).
  // ...but NOT for an account that was created moments ago (v16.55). A brand
  // new user IS a first-time visitor, and this effect was marking the tour
  // complete before they ever saw it - reported as "no app intro after signup".
  // register() sets justRegistered; it is cleared once the tour finishes or is
  // skipped, so a genuine returning user still gets the v16.08 protection.
  useEffect(() => {
    if (user && safeGet('justRegistered') !== 'true') {
      safeSet('onboardingComplete', 'true');
    }
  }, [user]);

  // One silent refresh for the first load (V19.12, live test 19.11 item 8:
  // a fresh tab showed the login page for a few seconds, then signed itself
  // in). /auth/ calls skip the 401 refresh above, so an expired access token
  // on /auth/me signed the person out while the refresh cookie still held.
  const refreshForMe = async () => {
    // Not inside the phone preview frame (V19.19): an error with no answer
    // from the server is "try again later", never "signed out".
    if (isFramedPreview()) throw new Error("phone preview: the page around it renews the sign-in");
    if (!refreshPromise) {
      refreshPromise = axios.post(`${API}/auth/refresh`, refreshBody(IS_NATIVE, safeGet("refreshToken")))
        .finally(() => { setTimeout(() => { refreshPromise = null; }, 0); });
    }
    const r = await refreshPromise;
    const newToken = r.data.token;
    safeSet("token", newToken);
    saveRefreshToken(r.data);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${newToken}` } });
    setToken(newToken);
    return me.data;
  };

  // ONLY A 401 SIGNS OUT (V19.13, audit A1). Any other failure here - offline,
  // a timeout, a 5xx, the server waking up - used to call logout(), which
  // cancelled the session on the server too. Now the pass is kept, the app
  // stays on its loading screen, and /auth/me is tried again with a backoff
  // (and the moment the phone is back online). Only a refresh that answers
  // 401 signs out.
  const fetchUser = async (attempt = 0) => {
    const startedWith = safeGet("token");
    const outcome = await loadSignedInUser({
      getMe: async () => (await axios.get(`${API}/auth/me`)).data,
      refreshAndGetMe: refreshForMe,
    });
    if (outcome.state === "signed_out") logout();
    if (outcome.state === "ok") setUser(outcome.user);
    if (outcome.state !== "retry") { setLoading(false); return; }
    let done = false;
    const again = () => {
      if (done) return;
      done = true;
      window.removeEventListener("online", again);
      // Signed out, or signed in afresh, meanwhile: that flow owns it now.
      if (safeGet("token") !== startedWith) return;
      fetchUser(attempt + 1);
    };
    window.addEventListener("online", again);
    setTimeout(again, meRetryDelay(attempt));
  };

  const handleSessionExpiredOk = () => {
    setShowSessionExpired(false);
    logout();
  };

  // Facebook: exchange the FB access token for our session.
  const loginWithFacebook = async (accessToken) => {
    const res = await axios.post(`${API}/auth/facebook`, { access_token: accessToken });
    safeSet("token", res.data.token);
    saveRefreshToken(res.data);
    safeSet("userEmail", res.data.user?.email || "");
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  // Google Identity Services: exchange the ID token for our session.
  const loginWithGoogle = async (credential) => {
    const res = await axios.post(`${API}/auth/google`, { credential });
    safeSet("token", res.data.token);
    saveRefreshToken(res.data);
    safeSet("userEmail", res.data.user?.email || "");
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  /**
   * Adopt a session the backend has already issued - used after a password
   * reset. The user has just proved control of their mailbox and chosen the
   * password, so sending them back to a login form is a pointless third step,
   * and the step where they get locked out by retyping it wrong.
   */
  // Does the SERVER already know who this person is?
  //
  // The old test was `!safeGet("memberName")` - i.e. "is localStorage empty".
  // That conflates "we have never met" with "this browser forgot", and the two
  // are not the same: a deploy that logs everyone out empties localStorage
  // while the account on the server is unchanged. The owner was then asked to
  // introduce himself again and the answer appended a SECOND member row with
  // the same name and number.
  //
  // The server has always had the answer in owner_name; it just was not being
  // sent. Now it is, so trust it, and rehydrate this browser from it instead
  // of asking a question that has already been answered.
  // ...AND WHEN THE SERVER SAYS NOTHING, THE PHONE IS NOT THE ANSWER EITHER.
  //
  // (1 October 2026, from the Tradie's Bay fork, which hit it on a real phone)
  //
  // The line below used to be `return Boolean(safeGet("memberName"))`: if the
  // account had no name, fall back to whatever name this BROWSER happens to
  // hold. That is the same mistake as the one above, pointing the other way.
  // On a phone that has changed hands - or a shared family phone, or one
  // handed to a partner to sign in on - the leftover belongs to the PREVIOUS
  // person, and a blank account would silently take their name and colour
  // without ever asking. Paired with the backfill effect that used to live
  // further down, it then WROTE that name onto the new account.
  //
  //     A QUESTION ABOUT THE PERSON WAS ANSWERED FROM THE DEVICE.
  //
  // It worked for exactly as long as each person only ever used one phone
  // that never got cleared.
  //
  // So: the account is the key, not the browser. If the account has no name,
  // the leftover is removed - it was never this account's - and the question
  // is asked. Deliberately NOT a device id: the login is the person, and a
  // device number breaks the day someone gets a new phone.
  const adoptOwnerIdentity = (u) => {
    const serverName = (u?.owner_name || "").trim();
    if (serverName) {
      setMemberName(serverName);
      safeSet("memberName", serverName);
      if (u.owner_badge) {
        setMemberBadge(u.owner_badge);
        safeSet("memberBadge", u.owner_badge);
      }
      return true;
    }
    setMemberName("");
    setMemberBadge("");
    safeRemove("memberName");
    safeRemove("memberBadge");
    return false;
  };

  const adoptSession = (data) => {
    if (!data?.token || !data?.user) return false;
    safeSet("token", data.token);
    saveRefreshToken(data);
    safeSet("userEmail", (data.user.email || "").toLowerCase());
    setToken(data.token);
    setUser(data.user);
    if (!adoptOwnerIdentity(data.user)) setShowMemberSetup(true);
    return true;
  };

  // A fresh pass for THIS phone after the server signed every phone out -
  // changing the password does that (V18.96). Only the pass changes: who is
  // signed in, and as which family member, stays exactly as it was.
  const keepSession = (data) => {
    if (!data?.token) return false;
    safeSet("token", data.token);
    saveRefreshToken(data);
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    setToken(data.token);
    reRegisterThisPhonesPush();
    return true;
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email: email.toLowerCase(), password });
    safeSet("token", res.data.token);
    saveRefreshToken(res.data);
    safeSet("userEmail", email.toLowerCase());
    setToken(res.data.token);
    setUser(res.data.user);
    
    // Ask only if neither the server nor this browser knows who they are.
    if (!adoptOwnerIdentity(res.data.user)) {
      setShowMemberSetup(true);
    }
    
    return res.data;
  };

  const loginWithPin = async (email, pin) => {
    const res = await axios.post(`${API}/auth/login-pin`, { email: email.toLowerCase(), pin });
    safeSet("token", res.data.token);
    saveRefreshToken(res.data);
    safeSet("userEmail", email.toLowerCase());
    setToken(res.data.token);
    setUser(res.data.user);
    
    // Ask only if neither the server nor this browser knows who they are.
    if (!adoptOwnerIdentity(res.data.user)) {
      setShowMemberSetup(true);
    }
    
    return res.data;
  };

  // Quick-access login: redeem a 6-digit single-use code for a full session.
  // Always nudges member setup afterwards - a relative logging in this way is
  // on a fresh phone and should pick their name + badge.
  const quickLogin = async (code) => {
    const res = await axios.post(`${API}/auth/quick-login`, { code });
    safeSet("token", res.data.token);
    saveRefreshToken(res.data);
    if (res.data.user?.email) safeSet("userEmail", res.data.user.email);
    setToken(res.data.token);
    setUser(res.data.user);

    if (!adoptOwnerIdentity(res.data.user)) {
      setShowMemberSetup(true);
    }

    return res.data;
  };

  const register = async (email, password, turnstileToken) => {
    // Returns { requires_verification: true } - account not active yet.
    // Family name is derived server-side and editable later in Settings.
    // turnstileToken is only sent when Turnstile is configured (v15.23); the
    // backend ignores it while dormant and requires it once enabled.
    const res = await axios.post(`${API}/auth/register`, {
      email: email.toLowerCase(),
      password,
      // The app language goes with the form, because the very first thing
      // this account receives is the verification email and there has been no
      // Settings visit to learn it from. Every account email was English for
      // everybody until v18.44. (Settings keeps it updated from then on.)
      language: getLanguage(),
      ...(turnstileToken ? { turnstile_token: turnstileToken } : {})
    });
    // Don't log in - they need to verify email first
    // Save email so verification screen can show it
    safeSet("pendingVerifyEmail", email.toLowerCase());
    // Mark this as a genuinely new account so the first-run tour still shows.
    // Cleared by OnboardingPage when the tour is finished or skipped.
    safeSet("justRegistered", "true");
    safeRemove("onboardingComplete");
    
    return res.data;
  };

  const verifyEmail = async (token) => {
    const res = await axios.post(`${API}/auth/verify-email`, { token });
    safeRemove("pendingVerifyEmail");
    return res.data;
  };

  const resendVerification = async (email) => {
    const res = await axios.post(`${API}/auth/resend-verification`, { email: email.toLowerCase() });
    return res.data;
  };

  const changeEmail = async (newEmail, currentPassword) => {
    const res = await axios.post(`${API}/auth/change-email`, {
      new_email: newEmail.toLowerCase(),
      current_password: currentPassword
    });
    // Refresh user state to show pending change
    setUser({ ...user, pending_email_change: newEmail.toLowerCase() });
    return res.data;
  };

  const setPin = async (pin) => {
    await axios.post(`${API}/auth/set-pin`, { pin });
    setUser({ ...user, has_pin: true });
    safeSet("hasPinEnabled", "true");
    setShowPinSetup(false);
  };

  const skipPinSetup = () => {
    setShowPinSetup(false);
  };

  // Persist the owner's display name + badge to the backend so they show up
  // in the shared members list on every family device (best-effort).
  const syncOwnerProfile = async (name, badge) => {
    if (!name) return;
    // Genuinely best-effort: the name is already saved locally and this only
    // mirrors it to the other family devices. No toast - but it has to be
    // visible to whoever is looking at a console, or a broken sync is
    // undiscoverable. (10 Sep 2026)
    try {
      await axios.put(`${API}/auth/profile`, { name, badge: badge || null });
      return true;
    } catch (e) {
      // Still no toast from this context - the caller decides. But it now
      // RETURNS the outcome, because this is the write that stops the setup
      // modal reappearing, and a caller that cannot tell whether it landed
      // will keep telling the user everything is fine. (19 Sep 2026)
      console.error("owner profile sync failed", e);
      return false;
    }
  };

  // THE BACKFILL EFFECT USED TO LIVE HERE. IT IS GONE ON PURPOSE.
  //
  //     useEffect(() => {
  //       if (token && memberName) syncOwnerProfile(memberName, memberBadge);
  //     }, [token, memberName, memberBadge]);
  //
  // It existed to help accounts created before owner-profile storage, which
  // had their name only in localStorage. What it actually did was push THIS
  // BROWSER's name onto WHICHEVER ACCOUNT was signed in - so on a phone that
  // changes hands it wrote the last person's name onto the next person's
  // account, silently, with no one having typed anything. (1 Oct 2026, from
  // the Tradie's Bay fork.)
  //
  // Nothing is lost. An account with a name on the server is adopted by
  // adoptOwnerIdentity. An account without one is now ASKED, and
  // completeMemberSetup writes the answer to the server and reports failure.
  // updateMemberInfo still syncs when the person edits their own details.
  // Every write to the account now follows from something the person did.
  //
  //     A WRITE NOBODY ASKED FOR IS NOT A BACKFILL. IT IS A GUESS WITH A
  //     DATABASE BEHIND IT.

  const completeMemberSetup = async (name, phone, badge) => {
    // Returns true only if everything landed. The caller decides what to show;
    // this context has no toast of its own. (10 Sep 2026)
    let addMemberFailed = false;
    // Save locally
    setMemberName(name);
    setMemberPhone(phone || "");
    if (badge) {
      setMemberBadge(badge);
      safeSet("memberBadge", badge);
    }
    safeSet("memberName", name);
    if (phone) safeSet("memberPhone", phone);
    // AWAITED, AND ITS FAILURE IS REPORTED (19 Sep 2026).
    //
    // This was fire-and-forget with a console.error, and that is what made the
    // setup modal come back. The server's owner_name is the ONLY thing that
    // stops it being asked again: adoptOwnerIdentity trusts it, and falls back
    // to localStorage, which a logout empties. So if this PUT fails, the name
    // is saved on the device and nowhere else, the next sign-in concludes it
    // has never met this person, and the question is asked again - with
    // nothing anywhere telling the user their answer did not stick.
    //
    // Ivan hit exactly that: repeatedly asked for a name and colour he had
    // already given. Awaiting it and reporting failure turns a silent loop
    // into one visible error.
    const ownerSynced = await syncOwnerProfile(name, badge);
    
    // Add as family member to backend (if phone provided)
    if (phone) {
      try {
        await axios.post(`${API}/auth/add-member`, { name, phone, email: null });
      } catch (e) {
        // "Already a member" is the expected, harmless case. Anything else
        // means the member was NOT added while the UI carried on as if it
        // had. This used to swallow both alike. (10 Sep 2026)
        const status = e?.response?.status;
        if (status !== 409) {
          console.error("add-member failed", status, e);
          addMemberFailed = true;
        }
      }
    }
    
    setShowMemberSetup(false);
    // The owner sync counts toward success too. Reporting success when only
    // the device knows the name is what left Ivan answering the same question
    // after every sign-in. (19 Sep 2026)
    return ownerSynced && !addMemberFailed;
  };

  const skipMemberSetup = () => {
    setShowMemberSetup(false);
  };

  const updateMemberInfo = (name, phone, badge) => {
    setMemberName(name);
    setMemberPhone(phone || "");
    safeSet("memberName", name);
    if (phone) safeSet("memberPhone", phone);
    if (badge) {
      setMemberBadge(badge);
      safeSet("memberBadge", badge);
    }
    syncOwnerProfile(name, badge);
  };

  /**
   * End the session.
   *
   * `forget` separates two things that were the same call and should not be.
   * (13 Sep 2026)
   *
   *   A SESSION ENDING BY ITSELF - expiry, or a token the server rejects -
   *   should keep the saved email and PIN flag. The same person is coming
   *   back and a four-digit PIN is the point of it.
   *
   *   PRESSING LOG OUT is a person saying they are done, or that this is the
   *   wrong account. Keeping their address then means the next visit shows a
   *   PIN screen for an account they just left, with no way past it. Ivan hit
   *   exactly that: an unverified account he could not sign into, and logging
   *   out did not clear it, so every visit returned to the same locked
   *   keypad.
   */
  const logout = ({ forget = false } = {}) => {
    // best-effort server-side revocation (cookie or stored native token).
    // This phone's push subscription goes with it (V19.13, audit B8): a
    // signed-out phone kept getting the account's pushes, budget amounts
    // included. The pass is read now, before it is removed below.
    const body = refreshBody(IS_NATIVE, safeGet("refreshToken"));
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    // Capped, so the sign-out itself is never held up (and cannot land after
    // a quick sign-in that set a new cookie).
    Promise.race([forgetThisPhonesPush(sw), new Promise((ok) => setTimeout(() => ok(null), 1000))])
      .then((endpoint) => axios.post(`${API}/auth/logout`, endpoint ? { ...body, push_endpoint: endpoint } : body))
      .catch(() => {});
    safeRemove("refreshToken");
    safeRemove("token");
    // Lists kept on this phone for offline use go too (V19.14). Changes still
    // waiting to be sent stay, marked with their account, and go the next
    // time that account signs in here - never under anyone else's.
    forgetListsOnPhone();

    if (forget) {
      // The device stops assuming who this is.
      safeRemove("userEmail");
      safeRemove("hasPinEnabled");
      safeRemove("familyName");
    }

    setToken(null);
    setUser(null);
    setShowSessionExpired(false);
    delete axios.defaults.headers.common["Authorization"];
  };

  // Get saved family name for pre-fill on re-login
  const getSavedFamilyName = () => safeGet("familyName") || "";
  const getSavedEmail = () => safeGet("userEmail") || "";

  return (
    <AuthContext.Provider value={{ 
      user, token, login, loginWithGoogle, loginWithFacebook, loginWithPin, quickLogin, register, verifyEmail, resendVerification, changeEmail, logout, loading, 
      setPin, showPinSetup, skipPinSetup, setUser,
      showMemberSetup, completeMemberSetup, skipMemberSetup,
      memberName, memberPhone, memberBadge, updateMemberInfo,
      showSessionExpired, handleSessionExpiredOk, getSavedFamilyName, adoptSession, keepSession,
      getSavedEmail,
      backendAwake,
      darkMode, toggleDarkMode
    }}>
      {children}
      
      {/* Session Expired Modal */}
      {showSessionExpired && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">{t('listsPage.sessionExpired')}</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{t('listsPage.sessionExpiredBody')}</p>
            <button
              onClick={handleSessionExpiredOk}
              className="w-full py-3 bg-black dark:bg-white text-white dark:text-black rounded-full font-semibold"
              data-testid="session-expired-ok-btn"
            >
              {t('common.ok')}
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
